import { useEffect, useRef, useState } from 'react'
import { gsap } from 'gsap'
import { useAppStore } from '../../state/useAppStore'
import { Zap, ChevronRight, Sparkles } from 'lucide-react'

const Portal = () => {
  const portalRef = useRef(null)
  const ringOuterRef = useRef(null)
  const ringMiddleRef = useRef(null)
  const ringInnerRef = useRef(null)
  const particlesRef = useRef(null)
  const buttonRef = useRef(null)
  const smokeRef = useRef(null)
  const titleRef = useRef(null)
  const subtitleRef = useRef(null)
  
  const { setPortalOpen, setPortalTransitioning } = useAppStore()
  const [isEngaging, setIsEngaging] = useState(false)
  const [particles, setParticles] = useState([])

  // Initialize particles for the vortex
  useEffect(() => {
    const newParticles = []
    for (let i = 0; i < 60; i++) {
      newParticles.push({
        id: i,
        size: Math.random() * 6 + 2,
        angle: Math.random() * Math.PI * 2,
        distance: Math.random() * 150 + 50,
        speed: Math.random() * 0.02 + 0.005,
        color: i % 3 === 0 ? '#00f7ff' : i % 3 === 1 ? '#ff00ff' : '#9d00ff'
      })
    }
    setParticles(newParticles)
  }, [])

  // GSAP animations on mount
  useEffect(() => {
    // Initial entrance animation
    const ctx = gsap.context(() => {
      // Portal container entrance
      gsap.from(portalRef.current, {
        duration: 1.5,
        scale: 0.5,
        opacity: 0,
        ease: "power4.out"
      })

      // Rings rotation
      gsap.to(ringOuterRef.current, {
        rotation: 360,
        duration: 20,
        repeat: -1,
        ease: "none"
      })

      gsap.to(ringMiddleRef.current, {
        rotation: -360,
        duration: 15,
        repeat: -1,
        ease: "none"
      })

      gsap.to(ringInnerRef.current, {
        rotation: 180,
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      })

      // Title animation
      gsap.from(titleRef.current, {
        duration: 1,
        y: 50,
        opacity: 0,
        delay: 0.5,
        ease: "back.out(1.7)"
      })

      gsap.from(subtitleRef.current, {
        duration: 1,
        y: 30,
        opacity: 0,
        delay: 0.8,
        ease: "power3.out"
      })

      // Button glow pulse
      gsap.to(buttonRef.current, {
        duration: 2,
        boxShadow: '0 0 40px rgba(0, 247, 255, 0.6), 0 0 60px rgba(157, 0, 255, 0.4)',
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut"
      })

      // Smoke particles animation
      if (smokeRef.current) {
        const smokeElements = smokeRef.current.querySelectorAll('.smoke-particle')
        smokeElements.forEach((el, i) => {
          gsap.to(el, {
            y: -200,
            opacity: 0,
            duration: 3 + Math.random() * 2,
            delay: i * 0.1,
            repeat: -1,
            ease: "power1.out"
          })
        })
      }
    })

    return () => ctx.revert()
  }, [])

  // Animate particles
  useEffect(() => {
    if (!particlesRef.current || particles.length === 0) return

    const ctx = gsap.context(() => {
      particles.forEach((particle, i) => {
        gsap.to(`.particle-${i}`, {
          rotation: particle.speed * 360 * 10,
          duration: 20,
          repeat: -1,
          ease: "none"
        })
      })
    }, particlesRef)

    return () => ctx.revert()
  }, [particles])

  const handleEngage = () => {
    if (isEngaging) return
    
    setIsEngaging(true)
    setPortalTransitioning(true)

    const ctx = gsap.context(() => {
      // Button explosion effect
      gsap.to(buttonRef.current, {
        scale: 5,
        opacity: 0,
        duration: 0.8,
        ease: "power4.out"
      })

      // Rings explode outward
      gsap.to([ringOuterRef.current, ringMiddleRef.current, ringInnerRef.current], {
        scale: 3,
        opacity: 0,
        duration: 1,
        stagger: 0.1,
        ease: "power4.out"
      })

      // Particles accelerate and fly out
      particles.forEach((_, i) => {
        gsap.to(`.particle-${i}`, {
          x: `+=${(Math.random() - 0.5) * 500}`,
          y: `+=${(Math.random() - 0.5) * 500}`,
          opacity: 0,
          duration: 1,
          delay: Math.random() * 0.3,
          ease: "power4.out"
        })
      })

      // Title and subtitle fade out
      gsap.to([titleRef.current, subtitleRef.current], {
        y: -50,
        opacity: 0,
        duration: 0.8,
        ease: "power3.in"
      })

      // Smoke burst
      if (smokeRef.current) {
        const smokeElements = smokeRef.current.querySelectorAll('.smoke-particle')
        smokeElements.forEach((el) => {
          gsap.to(el, {
            scale: 3,
            opacity: 0,
            duration: 1,
            ease: "power4.out"
          })
        })
      }

      // Portal container collapse
      gsap.to(portalRef.current, {
        scale: 0,
        opacity: 0,
        duration: 1,
        delay: 0.3,
        ease: "power4.in",
        onComplete: () => {
          setPortalOpen(false)
          setTimeout(() => setPortalTransitioning(false), 500)
        }
      })
    })

    return () => ctx.revert()
  }

  return (
    <div 
      ref={portalRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-monster-darker"
    >
      {/* Animated background gradient */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-monster-violet/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-monster-cyan/20 rounded-full blur-3xl" />
      </div>

      {/* Vortex particles */}
      <div ref={particlesRef} className="absolute inset-0">
        {particles.map((particle) => (
          <div
            key={particle.id}
            className={`particle-${particle.id} absolute`}
            style={{
              left: '50%',
              top: '50%',
              width: particle.size,
              height: particle.size,
              backgroundColor: particle.color,
              borderRadius: '50%',
              transform: `translate(-50%, -50%) rotate(${particle.angle}rad) translateX(${particle.distance}px)`,
              filter: 'blur(1px)',
              opacity: 0.7
            }}
          />
        ))}
      </div>

      {/* Main portal rings */}
      <div className="relative w-96 h-96">
        {/* Outer ring */}
        <svg
          ref={ringOuterRef}
          className="absolute inset-0"
          viewBox="0 0 200 200"
        >
          <circle
            cx="100"
            cy="100"
            r="95"
            fill="none"
            stroke="url(#outer-glow)"
            strokeWidth="2"
            strokeDasharray="5,5"
          />
          <defs>
            <radialGradient id="outer-glow">
              <stop offset="0%" stopColor="#00f7ff" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#9d00ff" stopOpacity="0.2" />
            </radialGradient>
          </defs>
        </svg>

        {/* Middle ring */}
        <svg
          ref={ringMiddleRef}
          className="absolute inset-0"
          viewBox="0 0 200 200"
        >
          <circle
            cx="100"
            cy="100"
            r="75"
            fill="none"
            stroke="url(#middle-glow)"
            strokeWidth="3"
          />
          <defs>
            <radialGradient id="middle-glow">
              <stop offset="0%" stopColor="#ff00ff" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#00ffd0" stopOpacity="0.3" />
            </radialGradient>
          </defs>
        </svg>

        {/* Inner ring */}
        <svg
          ref={ringInnerRef}
          className="absolute inset-0"
          viewBox="0 0 200 200"
        >
          <circle
            cx="100"
            cy="100"
            r="50"
            fill="none"
            stroke="url(#inner-glow)"
            strokeWidth="4"
            strokeDasharray="10,2"
          />
          <defs>
            <linearGradient id="inner-glow" gradientTransform="rotate(45)">
              <stop offset="0%" stopColor="#00f7ff" stopOpacity="1" />
              <stop offset="100%" stopColor="#ff00ff" stopOpacity="1" />
            </linearGradient>
          </defs>
        </svg>

        {/* Center glow */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-24 h-24 rounded-full bg-gradient-to-r from-monster-cyan via-monster-magenta to-monster-violet blur-xl opacity-70" />
          <div className="absolute w-16 h-16 rounded-full bg-gradient-to-r from-monster-cyan to-monster-violet animate-pulse" />
          <Sparkles className="absolute text-white/80 w-8 h-8" />
        </div>
      </div>

      {/* Smoke effect */}
      <div ref={smokeRef} className="absolute inset-0 pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="smoke-particle absolute left-1/2 top-1/2 w-4 h-4 bg-gradient-to-t from-gray-300/30 to-transparent rounded-full blur-sm"
            style={{
              transform: `translate(-50%, -50%) rotate(${i * 24}deg) translateY(-120px)`,
              opacity: 0.6
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center z-10">
        <h1 
          ref={titleRef}
          className="monster-title text-6xl md:text-7xl font-black mb-4 text-center"
        >
          MONSTERGEO <span className="text-monster-cyan">vX</span>
        </h1>
        
        <p 
          ref={subtitleRef}
          className="text-xl text-gray-300 mb-12 font-manrope font-light tracking-wider text-center px-8"
        >
          Futuristic Mapping & Commercial Intelligence Engine
        </p>

        {/* Engage button */}
        <button
          ref={buttonRef}
          onClick={handleEngage}
          disabled={isEngaging}
          className="monster-btn text-lg px-10 py-4 flex items-center gap-3 group relative overflow-hidden"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-monster-cyan/20 to-monster-violet/20 animate-pulse" />
          
          {isEngaging ? (
            <>
              <div className="w-5 h-5 border-2 border-transparent border-t-white rounded-full animate-spin" />
              <span className="font-orbitron">ENGAGING...</span>
            </>
          ) : (
            <>
              <Zap className="w-6 h-6 group-hover:rotate-12 transition-transform" />
              <span className="font-orbitron">ENGAGE ENGINE</span>
              <ChevronRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
            </>
          )}
          
          {/* Button particles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animation: `particles ${1 + Math.random() * 2}s infinite`
                }}
              />
            ))}
          </div>
        </button>

        {/* Loading indicators */}
        <div className="mt-12 flex items-center gap-6 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-monster-cyan animate-pulse" />
            <span>Initializing Quantum Mapper</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-monster-magenta animate-pulse" />
            <span>Loading Neural Networks</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-monster-turquoise animate-pulse" />
            <span>Syncing Data Streams</span>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="absolute bottom-8 left-0 right-0 text-center text-gray-500 text-sm font-manrope">
        © 2024 MONSTERGEO vX | Advanced Geographic Intelligence System
      </div>
    </div>
  )
}

export default Portal